import ChartModel from "./chart.model";
import GeneralUserModel from "./generaluser.model";
import MediaEntity from "./mediaEntity.model";
import WatchList from "./watchList.modal";
import PollStock from "./poleStock.model";
import { StockTagModel } from "./stocks.model";
import { PostPaperEntity } from "./papers.modal";
import { PostForumEntity } from "./forums.model";
import { PostPortfolio } from "./post.portfolio.model";
import { ColonyModel } from "./colony.model";
import storageService from "@/services/storage.service";

export interface SectionData {
  id: string;
  name: string;
  type: string;
  data: any;
}

export default class PostsModel {
  _id: string | undefined;
  createdOn: string | undefined;
  modifiedOn: number | undefined;
  colonyInfo: any | undefined;
  type: PostTypes | undefined | string = PostTypes.text;
  body: string | undefined = "";
  plainTextBody: string | undefined;
  media: MediaEntity[] | undefined = [];
  userId: string | undefined;
  profilePicture: number | undefined;
  coverPhoto: number | undefined;
  fullName: string | undefined;
  userName: string | undefined;
  userCountry: string | undefined;
  domain: PostDomain = PostDomain.stock;
  color: string | undefined;
  userGender: string | undefined;
  watchlistCategory: string = "";
  metaData: any[] = [];
  links: any[] = [];
  hashtags: string[] = [];
  userTags: GeneralUserModel[] = [];
  markets: string[] = [];
  pagePostData: any[] = [];
  stockTags: StockTagModel[] = [];
  suggestions: any = {};
  watchList: any | undefined = {};
  postPrivacy: PostPrivacy = PostPrivacy.everyone;
  repostId: string | undefined;
  chart: ChartModel | undefined = new ChartModel();
  pollStock: PollStock = new PollStock();
  quotedImage: number | undefined;
  feeling: string = "";
  paper: PostPaperEntity = new PostPaperEntity();
  forum: PostForumEntity = new PostForumEntity();
  postOnTwitter: boolean = false;
  postOnLinkedin: boolean = false;
  postUrl: string = "";
  sourceType: PostSourceType = PostSourceType.USER;
  portfolio: PostPortfolio | undefined;
  postSubType: string | undefined;
  colony: boolean = false;
  clubInfo: ClubInfo | undefined;
  sectionData: any;
  quotedText: any;
  userLocation: any;
  signal: SignalModel = new SignalModel();
  relations: any;
  trade: any;
  prediction: any;
  tempWatchlistStock: any[] = [];
  filters?: string[];
  circleIds: string[] = [];
  reactionCounts?: { [reactionType: string]: number };
  userReactions?: any[] = []; // Initialize as empty array
  totalReactions?: number | unknown;
  commentCount?: number;
  repostCount?: number;
  userHasReacted?: boolean;
  _isNew?: boolean = false;

  clearReactions() {
    this.reactionCounts = {};
    this.userReactions = [];
    this.totalReactions = 0;
    this.userHasReacted = false;
  }

  initializeReactionsFromEmbeddedData() {
    if (!this.reactionCounts) {
      this.reactionCounts = {};
    }
    if (!this.userReactions) {
      this.userReactions = [];
    }

    // Build reactionCounts from userReactions if counts are missing
    if (
      Object.keys(this.reactionCounts).length === 0 &&
      this.userReactions.length > 0
    ) {
      this.userReactions.forEach((reaction) => {
        if (reaction?.reactionType) {
          this.reactionCounts![reaction.reactionType] =
            (this.reactionCounts![reaction.reactionType] || 0) + 1;
        }
      });
    }

    const countsTotal = Object.values(this.reactionCounts).reduce(
      (sum: number, count: number) => sum + (Number(count) || 0),
      0
    );

    if (countsTotal > 0) {
      this.totalReactions = countsTotal;
    } else if (
      this.totalReactions === undefined ||
      this.totalReactions === null
    ) {
      this.totalReactions = 0;
    }

    this.updateUserHasReacted();
  }

  hasEmbeddedReactions(): boolean {
    const countsTotal = Object.values(this.reactionCounts || {}).reduce(
      (sum: number, count: number) => sum + (Number(count) || 0),
      0
    );
    return countsTotal > 0 || (Number(this.totalReactions) || 0) > 0;
  }

  updateFromReactionCountModel(reactionData: {
    reactionCounts?: { [reactionType: string]: number };
    count?: number;
  }) {
    if (!reactionData.reactionCounts) return;

    this.reactionCounts = { ...reactionData.reactionCounts };
    this.totalReactions =
      reactionData.count ??
      Object.values(this.reactionCounts).reduce(
        (sum, count) => sum + (Number(count) || 0),
        0
      );
  }

  updateReactions(reactionData: any, userData: any, clearExisting: boolean = true) {
    if (clearExisting) {
      this.clearReactions();
    }

    if (!this.reactionCounts) {
      this.reactionCounts = {};
    }


    // Update reaction counts
    Object.keys(reactionData).forEach(reactionType => {
      const newCount = reactionData[reactionType];
      const currentCount = this.reactionCounts![reactionType] || 0;

      if (newCount !== currentCount) {
        this.reactionCounts![reactionType] = newCount;
      }
    });

    this.totalReactions = Object.values(this.reactionCounts).reduce(
      (sum: number, count: number) => sum + count, 0
    );
    // User reactions can be updated less frequently since they're less critical
    if (userData && Object.keys(userData).length > 0) {
      this.updateUserReactions(userData);
    } else {
      // If no userData provided, try to reconstruct userReactions from reactionData structure
      if (!this.reactionCounts || !this.userReactions) return;
    }
    // console.log(`Reaction count for ${this.totalReactions } updated from ${ this.reactionCounts}`);

  }

  public updateUserReactions(userData: any) {
    if (!this.userReactions) {
      this.userReactions = [];
    }

    const existingReactionsMap = new Map(
      this.userReactions.map(reaction => [reaction.userId, reaction])
    );

    // Merge new reactions - userData should be an object with reaction types as keys
    Object.entries(userData).forEach(([reactionType, users]: [string, any]) => {
      if (Array.isArray(users)) {
        users.forEach((user: any) => {
          if (user && user.userId) {
            const userReaction = {
              ...user,
              reactionType: reactionType
            };
            existingReactionsMap.set(user.userId, userReaction);
          }
        });
      }
    });

    this.userReactions = Array.from(existingReactionsMap.values());

    // Update userHasReacted for current user
    this.updateUserHasReacted();
  }





  // Add this helper method
  public updateUserHasReacted() {
    const currentUserId = storageService.getUser()?._id;
    if (!currentUserId) {
      this.userHasReacted = false;
      return;
    }

    this.userHasReacted = this.userReactions?.some(
      reaction => reaction.userId === currentUserId
    ) || false;


  }


  updateCommentCount(count: number) {
    this.commentCount = count;
  }

  // Helper method to update repost count
  updateRepostCount(count: number) {
    this.repostCount = count;
  }


  // Helper to get users by reaction type
  getUsersByReactionType(reactionType: string) {
    return this.userReactions?.filter(reaction => reaction.reactionType === reactionType) || [];
  }

  // Helper to get unique users count by reaction type
  getUsersCountByReactionType(reactionType: string): number {
    return this.getUsersByReactionType(reactionType).length;
  }

  // Helper to get all unique users who reacted (for total count)
  getUniqueReactedUsers() {
    if (!this.userReactions) return [];

    const uniqueUsers = new Map();
    this.userReactions.forEach(reaction => {
      if (!uniqueUsers.has(reaction.userId)) {
        uniqueUsers.set(reaction.userId, reaction);
      }
    });

    return Array.from(uniqueUsers.values());
  }


  // Helper to add a user reaction
  addUserReaction(userReaction: any) {
    if (!this.userReactions) {
      this.userReactions = [];
    }

    // Remove existing reaction from this user
    this.userReactions = this.userReactions.filter(
      reaction => reaction.userId !== userReaction.userId
    );

    // Add new reaction
    this.userReactions.push(userReaction);

    // Update reaction counts
    if (!this.reactionCounts) {
      this.reactionCounts = {};
    }

    // Decrement old reaction type if user changed reaction
    // (This would need additional logic if tracking previous reactions)

    // Increment new reaction type
    this.reactionCounts[userReaction.reactionType] =
      (this.reactionCounts[userReaction.reactionType] || 0) + 1;

    this.totalReactions = Object.values(this.reactionCounts).reduce(
      (sum: number, count: number) => sum + count, 0
    );
  }
  // Helper to remove a user reaction
  removeUserReaction(userId: string) {
    if (!this.userReactions) return;

    const userReaction = this.userReactions.find(reaction => reaction.userId === userId);
    if (!userReaction) return;

    // Remove from user reactions
    this.userReactions = this.userReactions.filter(
      reaction => reaction.userId !== userId
    );

    // Update reaction counts
    if (this.reactionCounts && this.reactionCounts[userReaction.reactionType]) {
      this.reactionCounts[userReaction.reactionType]--;

      if (this.reactionCounts[userReaction.reactionType] <= 0) {
        delete this.reactionCounts[userReaction.reactionType];
      }

      this.totalReactions = Object.values(this.reactionCounts).reduce(
        (sum: number, count: number) => sum + count, 0
      );
    }
  }


  constructor() { }

  getColor() {
    return PostDomainColors[this.domain ?? PostDomain.stock];
  }

  getUserReactionType(userId: string): string | null {
    return this.userReactions?.[userId] || null;
  }
}

export class ClubInfo {
  _id: string | undefined;
  profileUrl: string | undefined;
  privacy: string | undefined;
  ownerId: string | undefined;
  name: string | undefined;
  membersCount: number | undefined;
  coverUrl: string | undefined;
  bio: string | undefined;
  category: string | undefined;
  tags: string[] | undefined;

  public static copyFromColony(colony: ColonyModel) {
    var club = new ClubInfo();
    club._id = colony._id;
    club.profileUrl = colony.profileUrl;
    club.privacy = colony.privacy;
    club.ownerId = colony.ownerId;
    club.name = colony.name;
    club.membersCount = colony.membersCount;
    club.coverUrl = colony.coverUrl;
    club.bio = colony.bio;
    club.category = colony.category;
    club.tags = colony.tags;
    return club;
  }
}

export enum PostSourceType {
  USER = "user",
  RSSFEED = "rssfeed",
  TWITTERTWEET = "twitter-tweet",
  DATATEAM = "data-team-tweets",
}

export enum PostPrivacy {
  everyone = "everyone",
  following = "following",
  friends = "friends",
  circles = "circles",
  //   subscribers = "subscribers",
}

export enum PostTypes {
  text = "text",
  media = "media",
  charts = "charts",
  watchList = "watchList",
  predicition = "predicition",
  repost = "repost",
  paper = "paper",
  club = "club",
  analysis = "analysis",
  profile = "profile",
  portfolio = "portfolio",
}

export enum PostSubTypes {
  prediction = "prediction",
  watchlist = "watchlist",
  charts = "charts",
  signal = "signal",
  links = "links",
  topStats = "topStats",
  favouriteInvestment = "favouriteInvestment",
  portfolio = "portfolio",
}

export enum PostFilters {
  traderverse = "traderverse",
  following = "following",
  friends = "friends",
  subscription = "subscription",
  signal = "signal",
  news = "news",
}

export enum PostTags {
  stocks = "Stocks",
  crypto = "Crypto",
  nft = "NFT",
  otc = "OTC",
  spy = "SPY",
  short = "SHORT",
  amd = "AMD",
}

export enum PostDomain {
  stock = "stock",
  crypto = "crypto",
  nft = "nft",
  finance = "finance",
}

// export enum PostDomainColors
// {
//     stock = "#bcff00",
//     crypto = "#ff8800",
//     nft = "#c257ff",
//     finance = "#00fff6",
// }

export enum PostDomainColors {
  stock = "rgb(139, 119, 207)",
  crypto = "#ff8800",
  nft = "#c257ff",
  finance = "#00fff6",
}

export class SignalModel {
  stock: any | undefined;
  title: string | undefined;
  description: string | undefined;
  position: string | undefined;
}
